import { message, steps, stepsHeadings } from 'constants/constants';
import { useCallback, useEffect, useState } from 'react';
import { Card } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useAddProjectMutation,
  useGetProjectByIdMutation,
} from 'redux/services/project';
import ProjectChecklist from './ProjectChecklist';
import DocumentsUpload from './DocumentsUpload';
import Launch from './Launch';
import ProjectInfo from './ProjectInfo';
import Subtask from './Subtask/Subtask';
import Teams from './Teams';
import { onSubmitParams } from './types';
import { Steps } from 'components/common/Steps';
import { Project } from 'redux/projectSlice';
import { Loader } from 'components/common/Loader';

const ProjectSetup = () => {
  const { step, setupId, projectId: pId } = useParams();
  const projectId = setupId || pId;

  const navigate = useNavigate();
  const [projectDetails, setProjectDetails] = useState<Project | null>();
  const [refresh, setRefresh] = useState<boolean>(false);
  const [addProject] = useAddProjectMutation();
  const [getProjectById, { isLoading }] = useGetProjectByIdMutation();

  const onSubmit = async (params: onSubmitParams) => {
    let projectDetails: any;
    if (projectId) {
      projectDetails = await getProjectById(projectId);
    }
    const projectValues = {
      ...params.values,
      isDraft: params.draft,
    };
    const addBody = {
      values: projectId
        ? { ...projectDetails.data, ...projectValues }
        : projectValues,
    };
    const res: any = await addProject(addBody);
    if (res.data && !params.draft) {
      return true;
    } else if (res.data && params.redirectAfterSubmit && params.draft) {
      toast.success(message.saveStep);
      navigate(`/project/setup/${res.data.projectInfo.id}/${Number(step) + 1}`);
      getProjectDetails();
    } else {
      toast.error(res.error.data.message);
      return false;
    }
  };

  const getProjectDetails = useCallback(async () => {
    if (projectId) {
      const res: any = await getProjectById(projectId);
      if (res.data) {
        setProjectDetails(res.data);
      } else toast.error(res.error.data.message);
    }
  }, [getProjectById, projectId]);

  useEffect(() => {
    if (projectId) {
      getProjectDetails();
    }
  }, [projectId, getProjectDetails]);

  const handleUploadDoc = () => getProjectDetails();

  const renderPage = () => {
    switch (step) {
      case '1':
        return (
          <ProjectInfo
            onSubmitProject={onSubmit}
            projectDetails={projectDetails?.projectInfo ?? null}
          />
        );
      case '2':
        return <Teams />;
      case '3':
        return (
          <ProjectChecklist
            onSubmitProject={onSubmit}
            projectDetails={projectDetails?.checklistLibraryId ?? ''}
          />
        );
      case '4':
        return (
          <Subtask
            onSubmitProject={onSubmit}
            projectDetails={projectDetails?.tasks ?? []}
          />
        );
      case '5':
        return (
          <DocumentsUpload
            projectDetails={projectDetails?.documents ?? null}
            isLaunched={projectDetails?.isLaunched}
            setRefresh={setRefresh}
            refresh={refresh}
            onUploadDoc={handleUploadDoc}
          />
        );

      default:
        return (
          <Launch onSubmitProject={onSubmit} projectDetails={projectDetails} />
        );
    }
  };

  return (
    <div className="d-flex flex-column h-100 overflow-auto">
      <div className="page-title text-center">
        <h2>{stepsHeadings[Number(step) - 1 || 0]}</h2>
      </div>
      <Steps
        steps={projectDetails?.isLaunched ? steps.slice(0, 5) : steps}
        allowAllStepsClickable={projectDetails?.isLaunched}
        activeStep={Number(step)}
        enableNavigatingPreviousSteps
        baseLink={`/project/setup/${projectId}`}
      />
      <Card className="project-setup-steps border-0 flex-grow-1 overflow-auto">
        <Card.Body className="h-100 d-flex flex-column">
          {renderPage()}
        </Card.Body>
        {isLoading && <Loader />}
      </Card>
    </div>
  );
};

export default ProjectSetup;
